import { useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info, Loader2, AlertTriangle } from 'lucide-react'

export type ToastType = 'info' | 'success' | 'error' | 'warning' | 'loading'

export interface UploadFileStatus {
  name: string
  status: 'uploading' | 'processing' | 'done' | 'error'
  error?: string
}

export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  progress?: number
  duration?: number
  dismissible?: boolean
  files?: UploadFileStatus[]
}

interface ToastProps {
  toast: Toast
  onDismiss: (id: string) => void
}

const iconMap = {
  info: Info,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  loading: Loader2
}

const colorMap = {
  info: 'alert-info',
  success: 'alert-success',
  error: 'alert-error',
  warning: 'alert-warning',
  loading: 'alert-info'
}

export default function ToastNotification({ toast, onDismiss }: ToastProps) {
  const Icon = iconMap[toast.type]
  const isDismissible = toast.dismissible !== false

  useEffect(() => {
    if (toast.duration && toast.duration > 0 && toast.type !== 'loading') {
      const timer = setTimeout(() => {
        onDismiss(toast.id)
      }, toast.duration)

      return () => clearTimeout(timer)
    }
  }, [toast.id, toast.duration, toast.type, onDismiss])

  return (
    <div className={`alert ${colorMap[toast.type]} shadow-lg mb-2 animate-slide-in-right`}>
      <div className="flex-1">
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 flex-shrink-0 ${toast.type === 'loading' ? 'animate-spin' : ''}`} />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold">{toast.title}</h3>
            {toast.message && (
              <p className="text-sm opacity-90 mt-1">{toast.message}</p>
            )}
            {typeof toast.progress === 'number' && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>Progress</span>
                  <span>{Math.round(toast.progress)}%</span>
                </div>
                <progress
                  className="progress progress-primary w-full"
                  value={toast.progress}
                  max="100"
                ></progress>
              </div>
            )}
            {toast.files && toast.files.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto">
                {toast.files.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    {file.status === 'uploading' && (
                      <Loader2 className="w-3 h-3 animate-spin flex-shrink-0 text-info" />
                    )}
                    {file.status === 'processing' && (
                      <Loader2 className="w-3 h-3 animate-spin flex-shrink-0 text-warning" />
                    )}
                    {file.status === 'done' && (
                      <CheckCircle className="w-3 h-3 flex-shrink-0 text-success" />
                    )}
                    {file.status === 'error' && (
                      <AlertCircle className="w-3 h-3 flex-shrink-0 text-error" />
                    )}
                    <span className="truncate flex-1" title={file.name}>
                      {file.name}
                    </span>
                    {file.error && (
                      <span className="text-error text-[10px]" title={file.error}>
                        ({file.error})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {isDismissible && (
            <button
              onClick={() => onDismiss(toast.id)}
              className="btn btn-sm btn-ghost btn-circle flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-70 w-96 max-w-[calc(100vw-2rem)]">
      {toasts.map((toast) => (
        <ToastNotification key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}
